/**
 * @voltxampere/engine — Transient stamp function contract
 *
 * Sprint 6 extends the Sprint 5 {@link DCStampContext} with the
 * extra fields a transient companion stamp needs:
 *
 *   - the step size `h` (the integrator picks it adaptively, so the
 *     per-iteration value isn't known until the controller decides);
 *   - the integration method (`"BE" | "TRAP" | "Gear-2"`);
 *   - the previous-step solution `xPrev` (already on the DC context)
 *     and the step-before-that `xPrevPrev` (added here, may be `null`
 *     on the very first step);
 *   - a `firstStep` flag (true at t = 0 — Gear-2 falls back to BE);
 *   - a `previousCurrent(componentId)` accessor for stamps that need
 *     a quantity not derivable from the node-voltage vector. Today
 *     only the TRAP capacitor reads this (its Ieq depends on
 *     `i_C(t-h)`); inductors get their previous current straight
 *     from the augmented MNA row, so they don't need the accessor.
 *
 * Sprint 7 unification: the `TransientStampDefinition` envelope and
 * its `defineTransientStamp` helper are retired in favour of a
 * single {@link StampDefinition} per component type (see
 * `stamps/definition.ts`).  The legacy names are kept here as thin
 * aliases so one-commit migrations across the workspace stay
 * reviewable.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
/**
 * Sprint 6 positional helper that wraps a standalone transient
 * stamp as a full {@link StampDefinition}, using the transient
 * function *also* as the DC apply. That matches the Sprint 6
 * passthrough semantics (DC and transient shared one function for
 * linear time-invariant elements). Modules that actually
 * differentiate DC vs transient behaviour (Sprint 6 capacitor /
 * inductor; Sprint 7 OpAmp) build their definition through
 * {@link defineStamp} directly.
 */
export function defineTransientStamp(type, apply, augments) {
    // TransientStampFn accepts TransientStampContext; DCStampFn
    // accepts the narrower DCStampContext. Since every transient
    // context IS a DC context, the transient fn is callable with a DC
    // context — TS's strict-function-types accepts this direction.
    const applyDC = apply;
    return { type, augments, applyDC, applyTransient: apply };
}
//# sourceMappingURL=types.js.map